import React, { useLayoutEffect, useRef, useState } from 'react';
import { validateFields } from './utils/validation';
import PageHeader from './components/PageHeader';
import MarketsSelector from './MarketsSelector';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { FileField, SelectField, TextAreaField, TextField } from './components/FormFields';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import ProductPreviewCard from './ProductPreviewCard';
import MarkdownEditorField from "./components/MarkdownEditorField";
import { ToastInline } from './components/ToastNotifications';
import { getAllBrands, getBrandById } from './hooks/useBrands';
import { useImageUpload } from './hooks/useImageUpload';
import { getAllMarkets } from './hooks/useMarkets';
import { hasSelectedColoradoMarket } from './utils/productForm';
import {
  getProductCategories,
  getProductSubspecies,
} from './hooks/useProducts';

export default function AddProductDialog({ onClose, onSave, organizationId }) {
  const image = useImageUpload({ organizationId, kind: 'product' });
  const [form, setForm] = useState({
    Name: '',
    BrandId: '',
    markets: [],
    ProductCategoryTypeId: '',
    Strain: '',
    Description: '',
    InventoryUpc: '',
    Notes: '',
    SubspeciesId: null,
    shelfLifeDays: '',
    ShowExpirationDateWhenAvailable: false,
    ShowUseByDateWhenAvailable: false,
    ShowSellByDateWhenAvailable: false,
  });

  const [errors, setErrors] = useState<Record<string, any>>({});
  const { close, isClosing } = useAnimatedClose(onClose);
  const [brandOptions, setBrandOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subspeciesOptions, setSubspeciesOptions] = useState([]);
  const optionLoadRequestIdRef = useRef(0);
  const brandMarketsRequestIdRef = useRef(0);

  const schema = [
    { name: 'Name', required: true, message: 'Product name is required' },
      { name: 'BrandId', required: true, message: 'Brand is required' },
      { name: 'ProductCategoryTypeId', required: true, message: 'Category is required' },
      {
          name: 'markets',
          validate: (markets) => {
              return !markets.some(m => m.selected);
          },
          message: 'Select at least one market'
      },

    ];

    useLayoutEffect(() => {
        const requestId = ++optionLoadRequestIdRef.current;
        brandMarketsRequestIdRef.current += 1;
        image.reset();
        setBrandOptions([]);
        setCategoryOptions([]);
        setSubspeciesOptions([]);
        setErrors({});
        setForm((current) => ({
          ...current,
          BrandId: '',
          markets: [],
          ProductCategoryTypeId: '',
          SubspeciesId: null,
        }));
        if (!organizationId) {
            return;
        }

        async function loadBrands() {
            const brandsResp = await getAllBrands(organizationId);
            if (requestId === optionLoadRequestIdRef.current) {
              setBrandOptions(Array.isArray(brandsResp) ? brandsResp : []);
            }
        }

        async function loadCategories() {
            const categoriesResp = await getProductCategories({ organizationId });
            if (requestId === optionLoadRequestIdRef.current) {
              setCategoryOptions(Array.isArray(categoriesResp) ? categoriesResp : []);
            }
        }

        async function loadProductSubspecies() {
            const subspeciesResp = await getProductSubspecies({ organizationId });
            if (requestId === optionLoadRequestIdRef.current) {
              setSubspeciesOptions(Array.isArray(subspeciesResp) ? subspeciesResp : []);
            }
        }

        loadProductSubspecies();
        loadBrands();
        loadCategories();

        return () => {
          optionLoadRequestIdRef.current += 1;
          brandMarketsRequestIdRef.current += 1;
        };
    }, [organizationId]);

    const update = async (field, value) => {
        if (field !== 'BrandId') {
            setForm((f) => ({ ...f, [field]: value }));
            return;
        }

        const requestId = ++brandMarketsRequestIdRef.current;
        setForm((f) => ({ ...f, BrandId: value, markets: [] }));

        if (organizationId && value) {
            const [brand, allMarkets] = await Promise.all([
              getBrandById(organizationId, value),
              getAllMarkets(organizationId),
            ]);
            if (requestId !== brandMarketsRequestIdRef.current) return;
            const brandMarketIds = Array.isArray(brand?.marketIds) ? brand.marketIds : [];
            const finalMarkets = (Array.isArray(allMarkets) ? allMarkets : [])
              .filter(m => brandMarketIds.includes(m.id))
              .map(m => ({ ...m, selected: true }));
            setForm((current) => ({ ...current, markets: finalMarkets }));
        }
    }

  const handleSave = () => {
      if (image.isPending) return;
      const { errors: nextErrors, isValid } = validateFields(schema, form);
      setErrors(nextErrors);
      if (!isValid) return;
      const product = {
          ...form,
          isArchived: false,
          OrganizationId: organizationId,
          MarketIds: form.markets.filter(m => m.selected).map(m => m.id),
          shelfLifeDays: form.shelfLifeDays === '' ? null : Number(form.shelfLifeDays),
          ProductImageId: image.id,
      };
      delete product.markets;
      onSave(product);
  };


  return (
    <div className={`stack add-product-page page-slide-in${isClosing ? ' page-slide-out' : ''}`} data-testid="add-product">
      <PageHeader
        title="Add Product"
        subtitle="Products"
        actions={(
          <button className="button secondary" onClick={close} data-testid="add-product-close">Back</button>
        )}
      />

      <div className="drawer-body preview-right">
        <div className="drawer-preview">
          <ProductPreviewCard
            previewEnabled={true}
            name={form.Name}
            brand={brandOptions.find((b) => String(b.id) === String(form.BrandId))?.displayName || ''}
            category={categoryOptions.find((c) => String(c.id) === String(form.ProductCategoryTypeId))?.name || ''}
            strain={form.Strain}
            inventoryUpc={form.InventoryUpc}
            imageName={image.name}
            imageUrl={image.url}
            markets={form.markets.filter(m => m.selected).map(m => m.name || m.label || m.abbreviation || String(m.id))}
            description={form.Description}
            showStatusBadge={false}
            emptyBrandLabel="Brand Name"
            emptyNameLabel="Untitled Product"
            emptyMetaLabel="Category • Strain"
            showEmptyPlaceholders
            mode="add"
            container="card"
          />
        </div>

        <div className="drawer-controls">
            <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} testId="add-product-validation-error" />

            <div className="form-grid"> 
              <TextField
                label="Name"
                required
                value={form.Name}
                onChange={(e) => update('Name', e.target.value)}
                placeholder="Product name"
                error={errors.Name}
                testId="add-product-name"
                errorTestId="add-product-name-error"
              />
               
              <SelectField 
                label="Brand"
                required
                value={form.BrandId}
                onChange={(e) => update('BrandId', e.target.value)}
                error={errors.BrandId}
                testId="add-product-brand"
                errorTestId="add-product-brand-error"
              >
                <option value="" disabled>Select a brand</option>
               {brandOptions
                 .slice()
                 .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                 .map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </SelectField>

              <SelectField 
                label="Category"
                required
                value={form.ProductCategoryTypeId}
                onChange={(e) => update('ProductCategoryTypeId', e.target.value)}
                error={errors.ProductCategoryTypeId}
                testId="add-product-category"
                errorTestId="add-product-category-error"
              >
                <option value="" disabled>Select a category</option>
                {categoryOptions
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectField> 

            <SelectField
                label="Subspecies"
                value={form.SubspeciesId}
                onChange={(e) => update('SubspeciesId', subspeciesOptions.find(so => e.target.value == so.id)?.id || null)}
                error={errors.SubspeciesId}
                testId="add-product-subspecies"
                errorTestId="add-product-subspecies-error" 
            >
                <option>Select a subspecie</option>
                {subspeciesOptions
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                  .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </SelectField>

              <TextField
                label="Strain"
                value={form.Strain}
                onChange={(e) => update('Strain', e.target.value)}
                placeholder="Optional strain name"
                testId="add-product-strain"
              />

              <TextField
                label="Inventory UPC"
                value={form.InventoryUpc}
                onChange={(e) => update('InventoryUpc', e.target.value)}
                placeholder="Optional"
                testId="add-product-inventory-upc"
              />

              <TextField
                label="Shelf life (calendar days)"
                type="number"
                min="0"
                step="1"
                value={form.shelfLifeDays}
                onChange={(e) => {
                  if (e.target.value === '' || /^\d+$/.test(e.target.value)) update('shelfLifeDays', e.target.value);
                }}
                placeholder="Optional"
                testId="add-product-shelf-life-days"
              />

              <div data-testid="add-product-image-error">
                <ToastInline message={image.error} onDismiss={image.dismissError} />
              </div>
              <div data-testid="add-product-image">
                <FileField
                  key={image.fileInputKey}
                  label="Product Image"
                  accept=".jpg, .jpeg, .png, .gif"
                  onChange={(e) => image.select(e.target.files?.[0])}
                  selectedName={image.name}
                  helpText="Max size: 5 MB | Supported file types: .jpg, .png, and .gif"
                  removeAction={image.remove}
                />
              </div>

              <div data-testid="add-product-notes">
                <TextAreaField
                  label="Notes (Internal)"
                  rows={3}
                  value={form.Notes}
                  onChange={(e) => update('Notes', e.target.value)}
                  placeholder="Internal only"
                />
              </div>

              {!hasSelectedColoradoMarket(form.markets) && <div className="field full-span" data-testid="add-product-consumer-date-display">
                <label className="label">Consumer Date Display</label>
                {[
                  ['ShowExpirationDateWhenAvailable', 'Show Expiration Date When Available', 'expiration-date'],
                  ['ShowUseByDateWhenAvailable', 'Show Use By Date When Available', 'use-by-date'],
                  ['ShowSellByDateWhenAvailable', 'Show Sell By Date When Available', 'sell-by-date'],
                ].map(([key, label, testId]) => <label key={key} style={{ display: 'block', marginTop: 8 }} data-testid={`add-product-show-${testId}`}>
                  <input type="checkbox" checked={form[key]} onChange={(e) => update(key, e.target.checked)} data-testid={`add-product-show-${testId}-enabled`} /> {label}
                </label>)}
              </div>}

            {form.BrandId && <div className="field full-span">
                <MarketsSelector
                    options={form.markets || []}
                    onChange={(next) => update('markets', next)}
                    error={errors.markets}
                    required
                    testId="add-product-markets"
                />
            </div>}
            </div>

            <div style={{paddingBottom: '16px'}} data-testid="add-product-description">
              <MarkdownEditorField
                label={"Description"}
                value={form.Description}
                placeholder={"Short description"}
                onChange={(e) => update('Description', e)}
              />
            </div>

            <DialogFooter>
              <button className="button secondary" onClick={close} data-testid="add-product-cancel">Cancel</button>
<button className="button" onClick={handleSave} data-testid="add-product-save" disabled={image.isPending}>Save Product</button>
            </DialogFooter>
          </div>
        </div>
      </div>
    </div>
  );
}
